// =============================================================================
// api/media.ts
// Maneja TODOS los archivos del sistema — punto único de entrada para storage
// =============================================================================

import type {
  HotspotImage,
  HotspotImageDraftItem,
  Media,
  MediaFolder,
} from "@/types";
import type { PlaylistTrack } from "@/types/tour/tour.types";
import { convertToWebP } from "@/utils/imageOptimizer";
import { queryClient } from "@cl/queryClient";
import { supabase } from "@cl/supabase";
import { handleSupabaseError } from "./_error";
import { fetchStorageUsage } from "./overview";

// ── Buckets ───────────────────────────────────────────────────────────────────

const BUCKET = {
  media: "media", // contenido de tours (público)
  avatars: "avatars", // avatares de usuario (público)
  system: "assets_system", // assets del sistema, solo admin (público)
} as const;

// ── Helpers ───────────────────────────────────────────────────────────────────

const MAX_UPLOAD_SIZE = 200 * 1024 * 1024; // 200 MB

// Keys = los MediaFolder REALES (media.types.ts). Las keys viejas
// (rooms/floors/tours) no existían como folder ⇒ esos uploads caían al
// fallback ".bin".
const ALLOWED_EXTENSIONS: Record<MediaFolder, string[]> = {
  scenes: ["webp", "jpg", "jpeg", "png", "tif", "tiff"],
  "scene-thumbs": ["webp", "jpg", "jpeg", "png"],
  hotspots: ["webp", "jpg", "jpeg", "png", "mp3", "ogg", "wav", "m4a", "mp4", "webm", "mov"],
  "room-covers": ["webp", "jpg", "jpeg", "png", "mp3", "ogg", "wav", "m4a"],
  "floor-maps": ["webp", "jpg", "jpeg", "png"],
  "tour-covers": ["webp", "jpg", "jpeg", "png", "mp3", "ogg", "wav", "m4a"],
};

const buildMediaPath = (
  userId: string,
  folder: MediaFolder,
  file: File,
): string => {
  const ext = pickSafeExt(file, ALLOWED_EXTENSIONS[folder] ?? [], "bin");
  const unique = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  return `${userId}/${folder}/${unique}.${ext}`;
};

// ── Upload guards (shared by every direct-upload helper) ───────────────────────
// The bucket-level `allowed_mime_types` in config.toml is the hard server backstop;
// these client checks fail fast with a clear error and stop a spoofed extension
// from landing in the storage path (e.g. avatar.svg / doc.html).

const IMAGE_MIME = ["image/webp", "image/jpeg", "image/png"];
const IMAGE_EXT = ["webp", "jpg", "jpeg", "png"];
const SYSTEM_MIME = [...IMAGE_MIME, "image/svg+xml"];
const SYSTEM_EXT = [...IMAGE_EXT, "svg"];

/** Pick a known-safe lowercase extension from the filename, else `fallback`. */
const pickSafeExt = (file: File, allowed: string[], fallback: string): string => {
  const raw = (file.name.split(".").pop() ?? fallback).toLowerCase();
  return allowed.includes(raw) ? raw : fallback;
};

/** Reject before upload when the client-reported MIME isn't in the allowlist. */
const assertMime = (file: File, allowed: string[]): void => {
  // Empty type happens for some TIFFs / odd browsers — the extension guard covers those.
  if (file.type && !allowed.includes(file.type)) {
    throw new Error(`Unsupported file type: ${file.type || "unknown"}`);
  }
};

// ── Scan LiDAR (RoomPlan) ─────────────────────────────────────────────────────
// El import LiDAR se eliminó (2026-09-14); esto sólo limpia el .glb que algún
// tour viejo pueda tener todavía en storage (no tiene fila en `media`, así que
// el GC por filas nunca lo barre).

/** Best-effort cleanup of a tour's scan model (called from deleteTour). */
export const removeScanModel = async (path: string): Promise<void> => {
  await supabase.storage.from(BUCKET.media).remove([path]);
};

export const replaceMediaRecord = async (
  oldId: string | null | undefined,
  newFile: File | null | undefined,
  folder: MediaFolder,
  userId: string,
  tourId?: string,
): Promise<string | null | undefined> => {
  if (newFile === undefined) return undefined;

  if (oldId) {
    const { data } = await supabase
      .from("media")
      .select("*")
      .eq("id", oldId)
      .single();
    if (data) await deleteMedia(data);
  }

  if (newFile === null) return null;

  const asset = await uploadMedia(userId, newFile, folder, tourId);
  return asset.id;
};

// ── Media (contenido de tours) ────────────────────────────────────────────────

export const fetchUserMedia = async (
  tourId?: string | null,
): Promise<Media[]> => {
  let query = supabase.from("media").select("*");

  if (tourId === null) {
    query = query.is("tour_id", null);
  } else if (tourId !== undefined) {
    query = query.eq("tour_id", tourId);
  }

  const { data, error } = await query.order("created_at", { ascending: false });

  if (error) handleSupabaseError(error, "fetchUserMedia");
  return data as Media[];
};

export const assignMediaToTour = async (
  mediaId: string,
  tourId: string | null,
): Promise<void> => {
  const { error } = await supabase
    .from("media")
    .update({ tour_id: tourId })
    .eq("id", mediaId);
  if (error) handleSupabaseError(error, "assignMediaToTour");
};

export const uploadMedia = async (
  userId: string,
  file: File,
  folder: MediaFolder,
  tourId?: string,
  onProgress?: (pct: number) => void,
): Promise<Media> => {
  if (file.size > MAX_UPLOAD_SIZE) {
    throw new Error(`File too large. Maximum size is ${MAX_UPLOAD_SIZE / 1024 / 1024} MB.`);
  }

  const isImage = file.type.startsWith("image/") || /\.tiff?$/i.test(file.name);
  const isScene = folder === "scenes";

  onProgress?.(5);
  const fileHQ = isImage ? await convertToWebP(file, isScene ? 0.95 : 0.8, isScene ? 8192 : 4096) : file;
  onProgress?.(30);

  // Pre-check the account's storage cap before the (potentially large) upload.
  // The DB trigger check_storage_limit() is the hard backstop; this is for UX.
  const { usedBytes, limitBytes } = await fetchStorageUsage();
  if (limitBytes !== -1 && usedBytes + fileHQ.size > limitBytes) {
    throw new Error("storage_limit_exceeded");
  }

  const storagePath = buildMediaPath(userId, folder, fileHQ);
  const { error: uploadError } = await supabase.storage
    .from(BUCKET.media)
    // Timestamped+random filename = immutable content → 1-year cache. The
    // Supabase default (1h) forced repeat visitors to re-download every cover.
    .upload(storagePath, fileHQ, { cacheControl: "31536000" });
  if (uploadError) handleSupabaseError(uploadError, "uploadMedia/storage");
  onProgress?.(70);

  const {
    data: { publicUrl: urlHQ },
  } = supabase.storage.from(BUCKET.media).getPublicUrl(storagePath);

  let urlLQ = null;
  if (isImage && isScene) {
    const fileLQ = await convertToWebP(file, 0.3, 512);
    onProgress?.(80);
    const thumbPath = storagePath.replace(`${folder}/`, `${folder}/thumbs/`);

    const { error: thumbError } = await supabase.storage.from(BUCKET.media).upload(thumbPath, fileLQ, { cacheControl: "31536000" });
    onProgress?.(85);
    if (thumbError) {
      console.warn("uploadMedia: thumbnail upload failed:", thumbError.message);
    } else {
      const {
        data: { publicUrl },
      } = supabase.storage.from(BUCKET.media).getPublicUrl(thumbPath);
      urlLQ = publicUrl;
    }

    // Variante MID 4096 para GPUs móviles (maxTextureSize < 8192). Sin fila ni
    // columna propia: el viewer deriva `scenes/mids/<file>` de la URL HQ
    // (src/utils/panoVariant.ts) y cae a la HQ si el archivo no existe.
    const fileMid = await convertToWebP(file, 0.85, 4096);
    const midPath = storagePath.replace(`${folder}/`, `${folder}/mids/`);
    const { error: midError } = await supabase.storage.from(BUCKET.media).upload(midPath, fileMid, { cacheControl: "31536000" });
    onProgress?.(90);
    if (midError) {
      console.warn("uploadMedia: mid variant upload failed:", midError.message);
    }
  }

  onProgress?.(95);
  const { data, error: insertError } = await supabase
    .from("media")
    .insert({
      user_id: userId,
      tour_id: tourId ?? null,
      name: file.name,
      storage_path: storagePath,
      url: urlHQ,
      thumb_url: urlLQ,
      size: fileHQ.size,
      mime_type: fileHQ.type,
    })
    .select()
    .single();

  if (insertError) {
    await supabase.storage.from(BUCKET.media).remove([storagePath]);
    handleSupabaseError(insertError, "uploadMedia/insert");
  }

  invalidateMediaCaches(tourId);
  return data as Media;
};

// Punto único de reactividad: todo upload/delete de media (escena, sala, piso,
// cover, branding, playlist) pasa por acá — refresca la galería Multimedia
// (["tour","media",id]) y el medidor de almacenamiento sin tocar cada caller.
function invalidateMediaCaches(tourId?: string | null) {
  void queryClient.invalidateQueries({
    queryKey: tourId ? ["tour", "media", tourId] : ["tour", "media"],
  });
  void queryClient.invalidateQueries({ queryKey: ["storage-usage"] });
}

// ── Tour background-audio playlist ─────────────────────────────────────────────
// One `media` row per track (so storage GC + the media library see them). The
// ordered list is denormalized into tours.settings.playlist; audio_id mirrors
// track[0] for the legacy single-track view/thumbnail path.

/** A playlist row being edited: `media_id` set ⇒ already saved; `file` set ⇒ a
 *  pending upload. */
export interface PlaylistDraftItem {
  media_id?: string;
  url: string;
  name: string;
  file?: File;
  /** Stable editor-only key for drag-reorder + React keys (never persisted). */
  uid?: string;
}

/**
 * Reconcile an edited playlist against the previously-saved one: delete removed
 * tracks (row + storage), upload pending files, and return the final ordered
 * list. Uploads reuse the generic media pipeline (folder `tour-covers`, same as
 * the legacy single audio).
 */
export const persistTourPlaylist = async (
  draft: PlaylistDraftItem[],
  prev: PlaylistTrack[],
  userId: string,
  tourId: string,
): Promise<PlaylistTrack[]> =>
  reconcileMediaList(draft, prev, userId, tourId, "tour-covers");

/**
 * Same reconcile for a hotspot's ADDITIONAL carousel images
 * (hotspots.images jsonb — the primary img_id is not part of this list).
 */
export const persistHotspotImages = async (
  draft: HotspotImageDraftItem[],
  prev: HotspotImage[],
  userId: string,
  tourId: string,
): Promise<HotspotImage[]> =>
  reconcileMediaList(draft, prev, userId, tourId, "hotspots");

/** Shared jsonb-media-list reconcile: delete removed (row + storage), upload
 *  pending files, return the final ordered list. */
const reconcileMediaList = async (
  draft: { media_id?: string; url: string; name?: string; file?: File }[],
  prev: { media_id: string }[],
  userId: string,
  tourId: string,
  folder: MediaFolder,
): Promise<{ media_id: string; url: string; name: string }[]> => {
  const keptIds = new Set(
    draft.map((d) => d.media_id).filter((id): id is string => !!id),
  );
  const removed = prev.filter((p) => !keptIds.has(p.media_id));
  for (const r of removed) {
    const { data } = await supabase
      .from("media")
      .select("*")
      .eq("id", r.media_id)
      .single();
    if (data) await deleteMedia(data as Media);
  }

  const out: { media_id: string; url: string; name: string }[] = [];
  for (const d of draft) {
    if (d.file) {
      const asset = await uploadMedia(userId, d.file, folder, tourId);
      out.push({ media_id: asset.id, url: asset.url, name: d.name || asset.name });
    } else if (d.media_id) {
      out.push({ media_id: d.media_id, url: d.url, name: d.name ?? "" });
    }
  }
  return out;
};

export const deleteMedia = async (asset: Media): Promise<void> => {
  if (!asset.storage_path.startsWith("legacy/")) {
    const pathsToDelete = [asset.storage_path];
    // Derive variant paths from storage_path: insert "thumbs/"/"mids/" before
    // the filename, e.g. "userId/scenes/unique.webp" → "userId/scenes/thumbs/unique.webp".
    // remove() ignores paths that don't exist, so pushing them is harmless for
    // media without variants.
    const lastSlash = asset.storage_path.lastIndexOf("/");
    if (lastSlash !== -1) {
      const dir = asset.storage_path.slice(0, lastSlash);
      const name = asset.storage_path.slice(lastSlash + 1);
      if (asset.thumb_url) pathsToDelete.push(`${dir}/thumbs/${name}`);
      pathsToDelete.push(`${dir}/mids/${name}`);
    }
    const { error: storageError } = await supabase.storage
      .from(BUCKET.media)
      .remove(pathsToDelete);
    // Soft-fail: storage cleanup is best-effort; DB record deletion still proceeds
    if (storageError) {
      console.error("[media] deleteMedia storage error:", storageError);
    }
  }

  const { error: dbError } = await supabase
    .from("media")
    .delete()
    .eq("id", asset.id);
  if (dbError) handleSupabaseError(dbError, "deleteMedia");
  invalidateMediaCaches(asset.tour_id);
};


// uploadFavicon se BORRÓ 2026-09-01 (0 callers): decisión de producto — el
// favicon del viewer white-label = avatar del dueño, sin upload propio.

export const uploadBrandLogo = async (
  userId: string,
  tourId: string,
  file: File,
): Promise<string> => {
  assertMime(file, IMAGE_MIME);
  const ext = pickSafeExt(file, IMAGE_EXT, "png");
  const filePath = `${userId}/branding/${tourId}_logo.${ext}`;

  const { error } = await supabase.storage
    .from(BUCKET.media)
    .upload(filePath, file, { upsert: true });
  if (error) handleSupabaseError(error, "uploadBrandLogo");

  const {
    data: { publicUrl },
  } = supabase.storage.from(BUCKET.media).getPublicUrl(filePath);

  return `${publicUrl}?v=${Date.now()}`;
};


// ── Avatars ───────────────────────────────────────────────────────────────────

export const uploadAvatar = async (
  userId: string,
  file: File,
): Promise<string> => {
  assertMime(file, IMAGE_MIME);
  const ext = pickSafeExt(file, IMAGE_EXT, "png");
  // Timestamped name is always unique → no upsert. `upsert:true` triggers
  // INSERT ... ON CONFLICT DO UPDATE, which evaluates the avatars_update RLS
  // policy (USING-only, no WITH CHECK) and always fails — even with no conflict.
  const filePath = `${userId}_${Date.now()}.${ext}`;

  const { error } = await supabase.storage
    .from(BUCKET.avatars)
    // Timestamped filename = immutable → 1-year cache (default was 1h).
    .upload(filePath, file, { cacheControl: "31536000" });
  if (error) handleSupabaseError(error, "uploadAvatar");

  const {
    data: { publicUrl },
  } = supabase.storage.from(BUCKET.avatars).getPublicUrl(filePath);

  return publicUrl;
};

export const deleteAvatar = async (url: string): Promise<void> => {
  if (!url) return;
  const marker = `${BUCKET.avatars}/`;
  const idx = url.indexOf(marker);
  if (idx === -1) return;
  const filePath = decodeURIComponent(
    url.substring(idx + marker.length).split("?")[0],
  );
  const { error } = await supabase.storage
    .from(BUCKET.avatars)
    .remove([filePath]);
  // Soft-fail: avatar cleanup is best-effort
  if (error) {
    console.error("[media] deleteAvatar error:", error);
  }
};

// ── Admin: all media for a user ───────────────────────────────────────────────
// RLS `media_select_public` allows `is_admin()`, so an admin reads any user's
// media rows directly. Used by the user-detail admin gallery.
export const fetchMediaByUser = async (userId: string): Promise<Media[]> => {
  const { data, error } = await supabase
    .from("media")
    .select("*")
    .eq("user_id", userId)
    .order("created_at", { ascending: false });
  if (error) handleSupabaseError(error, "fetchUserMedia");
  return (data ?? []) as Media[];
};

// ── System Assets (solo admin) ────────────────────────────────────────────────

export const uploadSystemAsset = async (
  file: File,
  name: string,
): Promise<string> => {
  assertMime(file, SYSTEM_MIME);
  const ext = pickSafeExt(file, SYSTEM_EXT, "png");
  const filePath = `${name}.${ext}`;

  const { error } = await supabase.storage
    .from(BUCKET.system)
    .upload(filePath, file, { upsert: true });
  if (error) handleSupabaseError(error, "uploadSystemAsset");

  const {
    data: { publicUrl },
  } = supabase.storage.from(BUCKET.system).getPublicUrl(filePath);

  return publicUrl;
};